'use client';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { WorkflowAccordion } from './workflow-accordion';
import { WorkflowBookmark } from './workflow-bookmark';
import { handleDragEnd } from './utils/drag-and-drop-utils';
import { useWorkflows } from '@/providers/workflow-provider';
import { useEffect, useMemo } from 'react';

interface WorkflowsProps {
  readonly workEngagementId?: string | null;
  readonly isEditMode?: boolean;
  readonly isDashboard?: boolean;
}

export const Workflows = ({
  workEngagementId = null,
  isEditMode = false,
  isDashboard = false,
}: WorkflowsProps) => {
  const {
    setIsDashboard,
    userWorkflows,
    workEngagementWorkflows,
    setWorkflows,
    save,
  } = useWorkflows();

  // When rendering for a specific work engagement, use that workflow
  // Otherwise use user workflows
  const workflows = useMemo(() => {
    if (workEngagementId) {
      return workEngagementWorkflows?.find(
        (w) => w.work_engagement_id === workEngagementId
      );
    }
    return userWorkflows;
  }, [workEngagementId, workEngagementWorkflows, userWorkflows]);

  useEffect(() => {
    setIsDashboard(isDashboard);
  }, [isDashboard, setIsDashboard]);

  const folders = useMemo(() => {
    const folders = workflows?.folders ?? [];

    const filteredFolders = isDashboard
      ? folders.filter((f) => f.pinned)
      : folders;

    return [...filteredFolders];
  }, [workflows?.folders, isDashboard]);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col'>
        <DragDropContext
          onDragEnd={(result) =>
            void handleDragEnd({
              type: 'folder',
              result,
              workflows,
              setWorkflows,
              save,
            })
          }
        >
          <Droppable isDropDisabled={!isEditMode} droppableId='workflows'>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className='flex flex-col'
              >
                {workflows &&
                  folders.map((folder, index) => {
                    return (
                      <Draggable
                        key={folder.client_id}
                        draggableId={folder.client_id}
                        index={index}
                        isDragDisabled={!isEditMode}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <WorkflowAccordion
                              workEngagementId={workEngagementId}
                              isEditMode={isEditMode}
                              folder={folder}
                              isDashboard={isDashboard}
                              dragHandleProps={provided.dragHandleProps}
                            >
                              <DragDropContext
                                onDragEnd={(result) =>
                                  handleDragEnd({
                                    type: 'bookmark',
                                    result,
                                    workflows,
                                    setWorkflows,
                                    save,
                                  })
                                }
                              >
                                <Droppable
                                  isDropDisabled={!isEditMode}
                                  droppableId={folder.client_id}
                                  direction='horizontal'
                                >
                                  {(droppableProvided) => (
                                    <div
                                      {...droppableProvided.droppableProps}
                                      ref={droppableProvided.innerRef}
                                      className='flex flex-nowrap overflow-x-auto'
                                    >
                                      {(folder.child_bookmarks ?? []).length ===
                                      0 ? (
                                        <p className='text-xs text-muted-foreground'>
                                          Det finns inga sparade vyer under
                                          detta flöde.
                                        </p>
                                      ) : (
                                        (folder.child_bookmarks ?? []).map(
                                          (bookmark, bookmarkIndex) => {
                                            const isLastItem =
                                              bookmarkIndex ===
                                              (folder.child_bookmarks ?? [])
                                                .length -
                                                1;

                                            return (
                                              <Draggable
                                                key={bookmark.client_id}
                                                draggableId={bookmark.client_id}
                                                index={bookmarkIndex}
                                                isDragDisabled={!isEditMode}
                                              >
                                                {(
                                                  draggableProvided,
                                                  bookmarkSnapshot
                                                ) => (
                                                  <div
                                                    ref={
                                                      draggableProvided.innerRef
                                                    }
                                                    {...draggableProvided.draggableProps}
                                                  >
                                                    <WorkflowBookmark
                                                      workEngagementId={
                                                        workEngagementId
                                                      }
                                                      isEditMode={isEditMode}
                                                      isLastItem={isLastItem}
                                                      isDragging={
                                                        bookmarkSnapshot.isDragging
                                                      }
                                                      bookmark={bookmark}
                                                      dragHandleProps={
                                                        draggableProvided.dragHandleProps
                                                      }
                                                    />
                                                  </div>
                                                )}
                                              </Draggable>
                                            );
                                          }
                                        )
                                      )}
                                      {droppableProvided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            </WorkflowAccordion>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
};
